/**
 * Labels belong to the workspace (FRIDAY-105). `projectIds` lists the boards
 * a label is limited to; empty means every board.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface WorkspaceLabel {
  id: string
  workspaceId: string
  /** Boards the label is limited to. Empty means every board. */
  projectIds: string[]
  name: string
  color: string
  version: number
  archivedAt: string | null
  /** How many non-archived tasks carry the label. */
  taskCount: number
}

export interface WorkspaceLabelBoard {
  id: string
  key: string
  name: string
  archivedAt: string | null
  /** Whether the caller may write to this board. */
  canWrite: boolean
}

export interface WorkspaceLabelsPayload {
  labels: WorkspaceLabel[]
  boards: WorkspaceLabelBoard[]
  /** Whether the caller may create and edit labels. */
  canEdit: boolean
  total: number
  limit: number
  offset: number
}

export interface WorkspaceLabelsQuery {
  q?: string
  /** Only the labels exposed on this board. */
  projectId?: string
  includeArchived?: boolean
  limit?: number
  offset?: number
}

/** Every label of the workspace. */
export function useWorkspaceLabels(wsId: string | undefined, params: WorkspaceLabelsQuery = {}) {
  const { q, projectId, includeArchived, limit, offset } = params
  return useQuery({
    queryKey: [
      'workspace-labels',
      wsId,
      {
        q: q ?? '',
        projectId: projectId ?? '',
        includeArchived: !!includeArchived,
        limit: limit ?? 100,
        offset: offset ?? 0,
      },
    ],
    queryFn: () =>
      api<WorkspaceLabelsPayload>(`/api/v1/workspaces/${wsId}/labels`, {
        query: {
          q: q || undefined,
          projectId: projectId || undefined,
          includeArchived: includeArchived ? 'true' : undefined,
          limit: limit ?? 100,
          offset: offset ?? 0,
        },
      }),
    enabled: !!wsId,
  })
}

/** Label changes show on every board of the workspace. */
function useInvalidateLabels(wsId: string | undefined) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['workspace-labels', wsId] })
    void qc.invalidateQueries({ queryKey: ['board', wsId] })
  }
}

/** Create a workspace label. No `projectIds` means every board. */
export function useCreateLabel(wsId: string | undefined) {
  const invalidate = useInvalidateLabels(wsId)
  return useMutation({
    mutationFn: (input: { name: string; color: string; projectIds?: string[] }) =>
      api(`/api/v1/workspaces/${wsId}/labels`, {
        method: 'POST',
        body: input,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: invalidate,
  })
}

/** Rename, recolour, or change the boards of a label. */
export function useUpdateLabel(wsId: string | undefined) {
  const invalidate = useInvalidateLabels(wsId)
  return useMutation({
    mutationFn: (input: { labelId: string; patch: { name?: string; color?: string; projectIds?: string[] } }) =>
      api(`/api/v1/labels/${input.labelId}`, {
        method: 'PATCH',
        body: input.patch,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: invalidate,
  })
}

/** Archive a label (soft delete — the server sets `archivedAt`). */
export function useArchiveLabel(wsId: string | undefined) {
  const invalidate = useInvalidateLabels(wsId)
  return useMutation({
    mutationFn: (labelId: string) =>
      api(`/api/v1/labels/${labelId}`, {
        method: 'DELETE',
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: invalidate,
  })
}
